import React from 'react';
import { Document, Page, View, Text, StyleSheet, Font } from '@react-pdf/renderer';
import NotoSansJP from '../components/assets/fonts/NotoSansJP-Regular.otf';

Font.register({ family: 'DailyCashJapanese', src: NotoSansJP });

const s = StyleSheet.create({
  page: { fontFamily: 'DailyCashJapanese', fontSize: 8, color: '#111', padding: 18 },
  heading: { textAlign: 'center', fontSize: 13, marginBottom: 8 },
  top: { flexDirection: 'row', gap: 12, marginBottom: 12 },
  half: { width: '50%' },
  box: { borderTopWidth: 0.6, borderLeftWidth: 0.6, borderColor: '#333' },
  row: { flexDirection: 'row', minHeight: 16 },
  cell: { paddingHorizontal: 3, paddingVertical: 2, borderRightWidth: 0.6, borderBottomWidth: 0.6, borderColor: '#333' },
  cellText: { fontSize: 7.5 },
  bold: { backgroundColor: '#eee' },
  right: { textAlign: 'right' },
  sectionHeading: { fontSize: 10, textAlign: 'center', backgroundColor: '#eee', padding: 3, borderWidth: 0.6, borderColor: '#333' },
  twoColumns: { flexDirection: 'row', gap: 10, marginTop: 8 },
  col: { flex: 1 },
  tableSpacer: { height: 7 },
  footerBox: { borderWidth: 0.6, borderColor: '#222', padding: 7, marginTop: 9 },
  final: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 3 },
});

const currency = (num) => `¥${Math.round(Number(num) || 0).toLocaleString('ja-JP')}`;
const cellStyle = (width, extra = {}) => [s.cell, { width }, ...(Array.isArray(extra) ? extra : [extra])];

const Cells = ({ label, value, widths = ['65%', '35%'], bold = false }) => <View style={s.row}>
  <Text style={cellStyle(widths[0], bold ? s.bold : {})}>{label}</Text>
  <Text style={cellStyle(widths[1], [s.right, bold ? s.bold : {}])}>{value}</Text>
</View>;

const NoteTable = ({ title, rows, total, labelKey = 'label' }) => <View wrap={false}>
  <Text style={s.sectionHeading}>{title}</Text>
  <View style={s.box}>
    {rows.length ? rows.map((row, i) => <Cells key={i} label={row[labelKey] || '　'} value={currency(row.amount)} />)
      : <Cells label="　" value="¥0" />}
    <Cells label="合計" value={currency(total)} bold />
  </View>
</View>;

export default function DailyCashReportPdfDocument({ report: r }) {
  if (!r) return null;
  const purchaseRows = (r.purchases || []).filter(x => x.label || Number(x.amount));
  const buyRows = (r.buybacks || []).filter(x => x.label || Number(x.amount));
  const expenseRows = (r.expenses || []).filter(x => x.label || Number(x.amount));
  const moveRows = (r.movements || []).filter(x => x.label || Number(x.amount)).map(x => ({
    label: `${x.direction === 'in' ? '移動入金' : '移動出金'} ${x.label || ''}`,
    amount: (x.direction === 'in' ? 1 : -1) * (Number(x.amount) || 0),
  }));

  return <Document title={`日次精算表_${r.date}_レジ${r.registerId}`}>
    <Page size="B5" style={s.page}>
      <Text style={s.heading}>日次精算表　{r.date}　{r.registerName}</Text>
      <Text style={{ textAlign: 'center', fontSize: 7, marginBottom: 3 }}>
        集計：{r.periodStart ? r.periodStart.replace('T', ' ').slice(0, 16) : '初回'}
        {' ～ '}{r.periodEnd ? r.periodEnd.replace('T', ' ').slice(0, 16) : r.date}
      </Text>
      <View style={s.top}>
        <View style={s.half}>
          <View style={s.box}>
            <View style={s.row}>
              {[['種別','30%'],['本数','18%'],['枚数','18%'],['金額','34%']].map(([val, width]) => <Text key={val} style={cellStyle(width, s.bold)}>{val}</Text>)}
            </View>
            {r.denominations.map(x => <View style={s.row} key={x.denom}>
              <Text style={cellStyle('30%', s.right)}>{x.denom.toLocaleString('ja-JP')}</Text>
              <Text style={cellStyle('18%', s.right)}>{x.bundles}</Text>
              <Text style={cellStyle('18%', s.right)}>{x.pieces}</Text>
              <Text style={cellStyle('34%', s.right)}>{currency(x.amount)}</Text>
            </View>)}
            <View style={s.row}>
              <Text style={cellStyle('66%', s.bold)}>現金合計</Text>
              <Text style={cellStyle('34%', [s.bold, s.right])}>{currency(r.countedTotal)}</Text>
            </View>
          </View>
        </View>
        <View style={s.half}>
          <View style={s.box}>
            <Cells label="本日（前回清算繰越）" value={currency(r.today)} bold />
            <Cells label="売上合計" value={currency(r.salesTotal)} />
            <Cells label="移動入金" value={currency(r.movementIn)} />
            <Cells label="移動出金" value={currency(r.movementOut)} />
            <Cells label="現金返品" value={currency(r.cashRefund)} />
            <Cells label="利益" value={currency(r.profit)} />
            <Cells label="税" value={currency(r.tax)} />
            <Cells label="残金（端数）" value={currency(r.remainder)} />
            <Cells label="過不足" value={currency(r.overShort)} />
            <Cells label="翌日" value={currency(r.nextAmount)} bold />
          </View>
        </View>
      </View>
      <View style={s.twoColumns}>
        <View style={s.col}>
          <NoteTable title="販売（現金）" rows={r.categories} labelKey="name" total={r.salesTotal} />
          <View style={s.tableSpacer} />
          <NoteTable title="移動" rows={moveRows} total={r.movementIn - r.movementOut} />
        </View>
        <View style={s.col}>
          <NoteTable title="買取" rows={buyRows} total={r.buybackTotal} />
          <View style={s.tableSpacer} />
          <NoteTable title="仕入れ" rows={purchaseRows} total={r.purchaseTotal} />
          <View style={s.tableSpacer} />
          <NoteTable title="経費" rows={expenseRows} total={r.expensesTotal} />
        </View>
      </View>
      <View style={s.footerBox} wrap={false}>
        <View style={s.final}><Text>本日　{currency(r.today)}</Text><Text>利益　{currency(r.profit)}</Text><Text>税　{currency(r.tax)}</Text></View>
        <View style={s.final}><Text>現金合計　{currency(r.countedTotal)}</Text><Text>残金　{currency(r.remainder)}</Text><Text>過不足　{currency(r.overShort)}</Text></View>
        <View style={s.final}><Text>翌日のスタート金額　{currency(r.nextAmount)}</Text></View>
      </View>
    </Page>
  </Document>;
}
